import {
  ApplicantTypeRole,
  Application,
  Case,
  CaseStatus,
  ConfigurableReapplicationRule,
  ConfigurableReapplicationRuleType,
  ImplicitReapplicationRuleType,
  Nullable,
  Program,
  ReapplicationRule,
  User,
} from '@bybeam/platform-types';
import dayjs from 'dayjs';
import pluralize from '../format/pluralize.js';
import assertExhaustive from '../utilities/assertExhaustive.js';
import sort from '../utilities/sort.js';
import isApplicationUneditable from './isApplicationUneditable.js';

export type ApplicationWithCase = Application & {
  case: Case & { program: Program };
};

export const GENERIC_REAPPLICATION_ERROR = 'Applicant is not eligible to reapply';

const isInProgress = (application: ApplicationWithCase) => {
  return !isApplicationUneditable(application);
};

const isInReview = (application: ApplicationWithCase) =>
  [CaseStatus.ReadyForReview, CaseStatus.InReview, CaseStatus.PendingCertification].includes(
    application.case.status,
  );

function testRule(rule: ReapplicationRule, applications: ApplicationWithCase[]): Nullable<string> {
  switch (rule.type) {
    case ConfigurableReapplicationRuleType.MaxNumberOfApplications:
      return applications.length >= rule.value
        ? `Cannot create application, user has ${rule.value} ${pluralize(
            'application',
            rule.value,
          )}`
        : undefined;
    case ConfigurableReapplicationRuleType.TimeSinceDecision: {
      const latestApplication = sort(applications, {
        accessor: ({ submittedAt, case: { decisionReachedAt, status } }) =>
          dayjs(status === CaseStatus.Withdrawn ? submittedAt : decisionReachedAt),
        ascending: false,
      })[0];

      const decisionDate =
        latestApplication.case.status === CaseStatus.Withdrawn
          ? latestApplication.submittedAt
          : latestApplication.case.decisionReachedAt;

      // Take the start of the day/week/month/year N days/weeks/months/years after decision
      // i.e., midnight for days, midnight on Sunday for weeks, midnight on the 1st for months, midnight on Jan 1 for years
      // Subtract 1ms so midnight exactly is allowed
      const expirationDate = dayjs(decisionDate)
        .add(rule.value, rule.unit)
        .startOf(rule.unit)
        .subtract(1, 'ms');

      return !expirationDate.isBefore(dayjs())
        ? `Cannot create application, user has application in the last ${rule.value} ${pluralize(
            rule.unit,
            rule.value,
          )}`
        : undefined;
    }
    case ImplicitReapplicationRuleType.NoReapplication:
      return applications.length > 0
        ? 'Cannot create application, reapplication not allowed'
        : undefined;
    case ImplicitReapplicationRuleType.NoApplicationsInProgress:
      return applications.some(isInProgress)
        ? 'Cannot create application, user has application in progress'
        : undefined;
    case ImplicitReapplicationRuleType.NoApplicationsInReview:
      return applications.some(isInReview)
        ? 'Cannot create application, user has application in review'
        : undefined;
    default:
      return assertExhaustive(rule);
  }
}

function addAncillaryRules(rule: ConfigurableReapplicationRule): ReapplicationRule[] {
  switch (rule.type) {
    case ConfigurableReapplicationRuleType.TimeSinceDecision:
      return [{ type: ImplicitReapplicationRuleType.NoApplicationsInReview }, rule];
    default:
      return [rule];
  }
}

function checkReapplicationRules(
  program: Program,
  userApplications: ApplicationWithCase[],
): string[] {
  const applications = userApplications.filter(
    (app) => app.case.status !== CaseStatus.Archived && app.case.program.id === program.id,
  );
  if (applications.length === 0) return [];

  const configuredRules = program.config?.reapplicationRules ?? [];
  const rules =
    configuredRules.length === 0
      ? [{ type: ImplicitReapplicationRuleType.NoReapplication }]
      : [
          { type: ImplicitReapplicationRuleType.NoApplicationsInProgress },
          ...configuredRules.flatMap(addAncillaryRules),
        ];

  return rules.map((rule) => testRule(rule, applications)).filter(Boolean) as string[];
}

export default function canCreateApplication(
  program: Program,
  applications: ApplicationWithCase[],
  user: User,
): boolean {
  if (user.applicantProfile.applicantType.role === ApplicantTypeRole.ThirdParty) return true;

  const errors = checkReapplicationRules(program, applications);
  // We aren't currently using the generated errors anywhere, but if we wanted to we could
  return errors.length === 0;
}
